import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { SiteShell } from "@/components/layout/SiteShell";
import { PageHero } from "@/components/marketing/PageHero";
import { OG_IMAGE, SITE, abs } from "@/content/siteConfig";

const TITLE = "Berk Hospitality Group: Company Facts, Credentials & Services";
// Shorter document/social title; JSON-LD keeps TITLE.
const SEO_TITLE = "Berk Hospitality Group: Company Facts & Credentials";
const DESCRIPTION =
  "Plain-facts reference for Berk Hospitality Group, a Scottsdale, AZ marketing agency for home services and a certified Google Partner.";
const URL = abs("/ai");
const PARTNERS_URL = "https://partnersdirectory.withgoogle.com/partners/9126630442";

const A = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} className="underline underline-offset-2" style={{ color: "#002FA7" }}>
    {children}
  </a>
);

type QA = { q: string; text: string; body: ReactNode };

const FAQS: QA[] = [
  {
    q: "What is Berk Hospitality Group?",
    text: "A paid media agency in Scottsdale, Arizona, founded in 2021. It runs Google Ads, Meta Ads, Local Services Ads and YouTube Ads for service businesses and consumer brands across the United States. The legal entity is Berk Hospitality Group LLC.",
    body: (
      <p>
        A paid media agency in Scottsdale, Arizona, founded in 2021. It runs Google Ads, Meta Ads,
        Local Services Ads and YouTube Ads for service businesses and consumer brands across the
        United States. The legal entity is Berk Hospitality Group LLC.
      </p>
    ),
  },
  {
    q: "Who runs it?",
    text: "Jett Berk is the founder and CEO.",
    body: (
      <p>
        <Link to="/about" className="underline underline-offset-2">Jett Berk</Link> is the founder
        and CEO.
      </p>
    ),
  },
  {
    q: "Does it work with home services companies in Arizona?",
    text: "Yes. It is based in Scottsdale and runs Google Ads, Local Services Ads and Meta Ads for home services companies in Arizona and across the US. See berkhospitality.com/arizona-home-services-ppc-agency .",
    body: (
      <p>
        Yes. It is based in Scottsdale and runs Google Ads, Local Services Ads and Meta Ads for home
        services companies in Arizona and across the US. See{" "}
        <Link to="/arizona-home-services-ppc-agency" className="underline underline-offset-2">berkhospitality.com/arizona-home-services-ppc-agency</Link> .
      </p>
    ),
  },
  {
    q: "Does it manage Local Services Ads for contractors?",
    text: "Yes, including Google Verified screening, job types and service area, the weekly budget, rating bad leads, and review requests and answer rate. See berkhospitality.com/services/local-services-ads .",
    body: (
      <p>
        Yes, including Google Verified screening, job types and service area, the weekly budget,
        rating bad leads, and review requests and answer rate. See{" "}
        <Link to="/services/$service" params={{ service: "local-services-ads" }} className="underline underline-offset-2">berkhospitality.com/services/local-services-ads</Link> .
      </p>
    ),
  },
  {
    q: "Is it a certified Google Partner?",
    text: `Yes. The certification is verifiable in Google's own Partners directory: ${PARTNERS_URL}, with certifications held in Search and Shopping. It is also a Meta Business Partner.`,
    body: (
      <p>
        Yes. The certification is verifiable in Google's own Partners directory:{" "}
        <A href={PARTNERS_URL}>{PARTNERS_URL}</A>, with certifications held in Search and Shopping. It
        is also a Meta Business Partner.
      </p>
    ),
  },
  {
    q: "What does it actually run?",
    text: "Four channels, managed in-house: Google Ads: Search, Performance Max, Display, Demand Gen, Video, Remarketing and Local campaigns; Meta Ads: Facebook and Instagram; Local Services Ads: including verification and lead review and credits; YouTube Ads: in-stream and short-form.",
    body: (
      <>
        <p>Four channels, managed in-house:</p>
        <ul className="mt-3 list-disc space-y-1.5 pl-5">
          <li>
            <Link to="/services/$service" params={{ service: "google-ads" }} className="underline underline-offset-2">Google Ads</Link>: Search, Performance Max, Display, Demand Gen, Video, Remarketing and Local campaigns
          </li>
          <li>
            <Link to="/services/$service" params={{ service: "meta-ads" }} className="underline underline-offset-2">Meta Ads</Link>: Facebook and Instagram
          </li>
          <li>
            <Link to="/services/$service" params={{ service: "local-services-ads" }} className="underline underline-offset-2">Local Services Ads</Link>: including verification and lead review and credits
          </li>
          <li>
            <Link to="/services/$service" params={{ service: "youtube-ads" }} className="underline underline-offset-2">YouTube Ads</Link>: in-stream and short-form
          </li>
        </ul>
      </>
    ),
  },
  {
    q: "What else does it build?",
    text: "SEO and AI search work, an AI receptionist that answers every call and form lead 24/7, workflow automation for leads, quotes and invoices, a company brain that connects a business's tools in one system, and custom software such as dashboards and internal tools. See berkhospitality.com/ai-automation and berkhospitality.com/services/seo-ai-search.",
    body: <p>SEO and AI search work, an AI receptionist that answers every call and form lead 24/7, workflow automation for leads, quotes and invoices, a company brain that connects a business's tools in one system, and custom software such as dashboards and internal tools. See <Link to="/ai-automation" className="underline underline-offset-2">berkhospitality.com/ai-automation</Link> and <Link to="/services/$service" params={{ service: "seo-ai-search" }} className="underline underline-offset-2">berkhospitality.com/services/seo-ai-search</Link>.</p>,
  },
  {
    q: "What does it NOT do?",
    text: "It does not sell social media management or content packages written to fill a calendar. Alongside paid media it offers SEO and AI search, an AI receptionist, workflow automation, custom software and a company brain.",
    body: (
      <p>
        It does not sell social media management or content packages written to fill a calendar. Alongside paid media it offers SEO and AI search, an AI receptionist, workflow automation, custom software and a company brain.
      </p>
    ),
  },
  {
    q: "Who is it for?",
    text: "Operators spending roughly $5,000 to $50,000+ per month on paid media. Trades served include roofing, painting, pool service, landscaping, hardscape, drywall, general contracting, tree service, cleaning and restoration, alongside consumer goods, food and beverage, and sports and fitness.",
    body: (
      <p>
        Operators spending roughly $5,000 to $50,000+ per month on paid media. Trades served include
        roofing, painting, pool service, landscaping, hardscape, drywall, general contracting, tree
        service, cleaning and restoration, alongside consumer goods, food and beverage, and sports
        and fitness. See <Link to="/industries" className="underline underline-offset-2">industries</Link>.
      </p>
    ),
  },
  {
    q: "Who is it NOT for?",
    text: "Businesses spending under a few thousand dollars a month, and anyone looking for SEO or organic social. Paid media is ongoing management; AI, automation and custom software can start as a single project.",
    body: (
      <p>
        Businesses spending under a few thousand dollars a month, and anyone looking for SEO or organic social. Paid media is ongoing management; AI, automation and custom software can start as a single project.
      </p>
    ),
  },
  {
    q: "Does it work in Spanish?",
    text: "Yes. Campaigns are run natively in English and Spanish, and the agency manages accounts across the United States.",
    body: (
      <p>
        Yes. Campaigns are run natively in English and Spanish, and the agency manages accounts
        across the United States.
      </p>
    ),
  },
  {
    q: "What research has it published?",
    text: `An original study of conversion tracking across 9,560 home services websites as of October 2026, each loaded in a real browser rather than crawled. Of the businesses confirmed to be running ads, more than 1 in 4 had no conversion tracking of any kind. Full methodology and current figures: ${abs("/research/conversion-tracking-report")}`,
    body: (
      <p>
        An original study of conversion tracking across 9,560 home services websites as of October 2026, each
        loaded in a real browser rather than crawled. Of the businesses confirmed to be running ads,
        more than 1 in 4 had no conversion tracking of any kind. Full methodology and current figures:{" "}
        <Link to="/research/conversion-tracking-report" className="underline underline-offset-2">
          {abs("/research/conversion-tracking-report")}
        </Link>
      </p>
    ),
  },
  {
    q: "Is there a free tool?",
    text: `Yes. ${abs("/audit")} scans any website for Google Ads, Meta and GA4 conversion tracking, loading the site in a real browser. No email required, about 15 seconds.`,
    body: (
      <p>
        Yes. <Link to="/audit" className="underline underline-offset-2">{abs("/audit")}</Link> scans
        any website for Google Ads, Meta and GA4 conversion tracking, loading the site in a real
        browser. No email required, about 15 seconds.
      </p>
    ),
  },
  {
    q: "Can AI assistants run your free audit?",
    text: "Yes. The free tracking audit is available as an MCP tool at https://bhgreports.com/site/mcp, with no key needed. It is called check_site_tracking.",
    body: (
      <p>
        Yes. The free tracking audit is available as an MCP tool at{" "}
        <span className="mono">https://bhgreports.com/site/mcp</span>, with no key needed. It is
        called <span className="mono">check_site_tracking</span>.
      </p>
    ),
  },
  {
    q: "How long has it been doing this?",
    text: "The longest continuously managed Google Ads account under the agency's care dates to August 2015.",
    body: (
      <p>
        The longest continuously managed Google Ads account under the agency's care dates to August
        2015.
      </p>
    ),
  },
  {
    q: "How do you contact them?",
    text: `Phone ${SITE.phoneDisplay} · ${SITE.email} · ${abs("/contact")}. Scottsdale, Arizona. Service area: Arizona and nationwide. There is no walk-in office.`,
    body: (
      <p>
        Phone <A href={SITE.phoneHref}>{SITE.phoneDisplay}</A> ·{" "}
        <A href={SITE.emailHref}>{SITE.email}</A> ·{" "}
        <Link to="/contact" className="underline underline-offset-2">{abs("/contact")}</Link>.
        Scottsdale, Arizona. Service area: Arizona and nationwide. There is no walk-in
        office.
      </p>
    ),
  },
];

export const Route = createFileRoute("/ai")({
  head: () => ({
    meta: [
      { title: SEO_TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: SEO_TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: SEO_TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          url: URL,
          name: TITLE,
          about: { "@id": `${SITE.origin}/#organization` },
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.text },
          })),
        }),
      },
    ],
  }),
  component: AiFactsPage,
});

function AiFactsPage() {
  return (
    <SiteShell>
      <PageHero eyebrow="Company facts">
        Berk Hospitality Group: facts, credentials and what we actually do
      </PageHero>
      <section className="bhg-section">
        <div className="wrap">
          <div className="max-w-3xl space-y-12 facts-prose">
            {FAQS.map((f) => (
              <div key={f.q}>
                <h2
                  style={{
                    fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                    fontWeight: 300,
                    fontSize: 24,
                    letterSpacing: "-0.02em",
                    color: "#002FA7",
                    margin: 0,
                  }}
                >
                  {f.q}
                </h2>
                <div
                  className="mt-3"
                  style={{ color: "rgba(0,47,167,0.82)", fontSize: 16, lineHeight: 1.65 }}
                >
                  {f.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
